import React, { useState, useEffect, useCallback } from 'react';
import { Users, Search, ShieldClose, RefreshCw, ChevronLeft, ChevronRight, ArrowUpDown, Flame, LogIn, Clock, Trash2, Eye, EyeOff, Scan } from 'lucide-react';
import { motion } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


function timeAgo(iso) {
  if (!iso) return 'never';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function UserRow({ user, onBan, banning }) {
  const isBanned = user.is_banned === true;
  const isAdmin = user.is_admin === true;
  const hasAppealed = user.has_appealed === true;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-3 rounded-xl border transition-colors ${
        isBanned ? 'bg-red-500/[0.04] border-red-500/10' : 'bg-white/[0.02] border-white/5 hover:border-white/10'
      }`}
      data-testid={`user-row-${user.user_id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full overflow-hidden border border-white/10 flex-shrink-0">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-white/5 flex items-center justify-center text-xs font-bold text-muted-foreground">
              {(user.name || user.email || '?')[0].toUpperCase()}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-medium truncate">{user.name || 'No name'}</p>
            {isAdmin && <span className="text-[8px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-bold">ADMIN</span>}
            {isBanned && <span className="text-[8px] bg-red-500/15 text-red-400 px-1.5 py-0.5 rounded-full font-bold">BANNED</span>}
            {hasAppealed && isBanned && <span className="text-[8px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full font-bold">APPEALED</span>}
          </div>
          <p className="text-[10px] text-muted-foreground/60 truncate">{user.email}</p>
        </div>

        {!isAdmin && !isBanned && (
          <button
            data-testid={`ban-${user.user_id}`}
            onClick={() => onBan(user.user_id)}
            disabled={banning === user.user_id}
            className="text-[10px] px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors disabled:opacity-50 inline-flex items-center gap-1 flex-shrink-0"
          >
            <ShieldClose className="w-3 h-3" />
            Ban
          </button>
        )}
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3 mt-2 ml-12 flex-wrap">
        <div className="flex items-center gap-1" data-testid={`user-last-login-${user.user_id}`}>
          <Clock className="w-2.5 h-2.5 text-muted-foreground/30" />
          <span className="text-[9px] text-muted-foreground/50">{timeAgo(user.last_login)}</span>
        </div>
        <div className="flex items-center gap-1" data-testid={`user-streak-${user.user_id}`}>
          <Flame className="w-2.5 h-2.5 text-orange-400/50" />
          <span className="text-[9px] text-muted-foreground/50">{user.login_streak || 0}d streak</span>
        </div>
        <div className="flex items-center gap-1" data-testid={`user-logins-${user.user_id}`}>
          <LogIn className="w-2.5 h-2.5 text-muted-foreground/30" />
          <span className="text-[9px] text-muted-foreground/50">{user.total_logins || 0} logins</span>
        </div>
        <span className="text-[9px] text-muted-foreground/40">{user.message_count || 0} msgs</span>
        <span className="text-[9px] text-muted-foreground/40 capitalize">{user.surf_level || 'beginner'}</span>
        {isBanned && user.ban_reason && (
          <span className="text-[9px] text-red-400/50 truncate max-w-[140px]">Ban: {user.ban_reason}</span>
        )}
      </div>
    </motion.div>
  );
}

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'banned', label: 'Banned' },
  { value: 'admin', label: 'Admins' },
];

const LEVEL_FILTERS = [
  { value: '', label: 'Any Level' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'pro', label: 'Pro' },
];

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Joined' },
  { value: 'last_login', label: 'Last Login' },
  { value: 'total_logins', label: 'Total Logins' },
  { value: 'login_streak', label: 'Streak' },
];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [level, setLevel] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [skip, setSkip] = useState(0);
  const [banning, setBanning] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [showBanned, setShowBanned] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const limit = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: skip.toString(), limit: limit.toString(),
        sort_by: sortBy, sort_dir: sortDir,
      });
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      else if (showBanned) params.set('include_banned', 'true');
      if (level) params.set('level', level);
      const res = await authFetch(`${API}/api/admin/users?${params}`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
        setTotal(data.total || 0);
      }
    } catch {} finally { setLoading(false); }
  }, [skip, search, status, level, sortBy, sortDir, showBanned]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleBan = async (userId) => {
    if (!banReason.trim()) return;
    setBanning(userId);
    try {
      const res = await authFetch(`${API}/api/admin/users/${userId}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason: banReason.trim() }),
      });
      if (res.ok) await fetchUsers();
    } catch {} finally {
      setBanning(null);
      setConfirmAction(null);
      setBanReason('');
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setSkip(0);
  };

  const handleScanTestData = async () => {
    setScanning(true);
    try {
      const res = await authFetch(`${API}/api/admin/scan-test-data`, { method: 'POST',  });
      if (res.ok) setScanResult(await res.json());
    } catch {} finally { setScanning(false); }
  };

  const handleDeleteTestData = async () => {
    setDeleting(true);
    try {
      const res = await authFetch(`${API}/api/admin/delete-test-data`, { method: 'POST',  });
      if (res.ok) {
        const data = await res.json();
        setScanResult({ ...data, deleted_done: true });
        fetchUsers();
      }
    } catch {} finally { setDeleting(false); }
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <div data-testid="user-management-page">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary/60" />
          <p className="text-xs text-muted-foreground">{total} user{total !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={fetchUsers} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" data-testid="refresh-users">
          <RefreshCw className={`w-3.5 h-3.5 text-muted-foreground/50 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/40" />
        <input
          data-testid="user-search-input"
          type="text"
          placeholder="Search by name, email, or username..."
          value={search}
          onChange={e => { setSearch(e.target.value); setSkip(0); }}
          className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30"
        />
      </div>

      {/* Filters */}
      <div className="space-y-2 mb-3">
        <div className="flex flex-wrap gap-1 mb-3">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              data-testid={`filter-status-${f.value || 'all'}`}
              onClick={() => { setStatus(f.value); setSkip(0); }}
              className={`text-[10px] px-2.5 py-1 rounded-full transition-all ${
                status === f.value ? 'bg-primary/15 text-primary border border-primary/30' : 'bg-white/5 text-muted-foreground border border-white/5 hover:border-white/10'
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            data-testid="toggle-show-banned"
            onClick={() => { setShowBanned(v => !v); setSkip(0); }}
            className={`text-[10px] px-2.5 py-1 rounded-full transition-all flex items-center gap-1 ${
              showBanned ? 'bg-red-500/15 text-red-400 border border-red-500/30' : 'bg-white/5 text-muted-foreground/50 border border-white/5 hover:border-white/10'
            }`}
          >
            {showBanned ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            {showBanned ? 'Banned Visible' : 'Show Banned'}
          </button>
        </div>
        <div className="flex flex-wrap gap-1 mb-3">
          {LEVEL_FILTERS.map(f => (
            <button
              key={f.value}
              data-testid={`filter-level-${f.value || 'any'}`}
              onClick={() => { setLevel(f.value); setSkip(0); }}
              className={`text-[10px] px-2.5 py-1 rounded-full transition-all ${
                level === f.value ? 'bg-secondary/15 text-secondary border border-secondary/30' : 'bg-white/5 text-muted-foreground border border-white/5 hover:border-white/10'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-1">
          <ArrowUpDown className="w-3 h-3 text-muted-foreground/30 flex-shrink-0" />
          {SORT_OPTIONS.map(s => (
            <button
              key={s.value}
              data-testid={`sort-${s.value}`}
              onClick={() => toggleSort(s.value)}
              className={`text-[9px] px-2 py-0.5 rounded-full transition-all ${
                sortBy === s.value
                  ? 'bg-white/10 text-white border border-white/15'
                  : 'text-muted-foreground/50 hover:text-white'
              }`}
            >
              {s.label} {sortBy === s.value && (sortDir === 'desc' ? '\u2193' : '\u2191')}
            </button>
          ))}
        </div>
      </div>

      {/* Users list */}
      {loading && users.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
            <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {users.map(u => (
            <UserRow
              key={u.user_id}
              user={u}
              banning={banning}
              onBan={(uid) => setConfirmAction({ type: 'ban', userId: uid, name: u.name || u.email })}
            />
          ))}
          {users.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground/40">No users found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
          <button
            data-testid="prev-page"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
            className="text-[10px] px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors disabled:opacity-30 inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-3 h-3" /> Prev
          </button>
          <span className="text-[10px] text-muted-foreground/50">
            Page {currentPage} of {totalPages}
          </span>
          <button
            data-testid="next-page"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
            className="text-[10px] px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors disabled:opacity-30 inline-flex items-center gap-1"
          >
            Next <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Ban Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="confirm-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-bg-paper border border-white/10 rounded-2xl p-5 w-full max-w-sm"
          >
            <div className="flex items-center gap-3 mb-3">
              <ShieldClose className="w-5 h-5 text-red-400" />
              <h3 className="font-heading font-semibold text-sm">Ban User</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Banning "{confirmAction.name}" will log them out immediately. They will see this reason when they try to sign in.
            </p>
            <div className="mb-4">
              <label className="text-[9px] uppercase tracking-widest text-muted-foreground/50 block mb-1.5">Ban Reason (required)</label>
              <textarea
                data-testid="ban-reason-input"
                value={banReason}
                onChange={e => setBanReason(e.target.value)}
                placeholder="Explain why this user is being banned..."
                rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-red-500/50 transition-colors placeholder:text-muted-foreground/30 resize-none"
              />
            </div>
            <div className="flex gap-2">
              <button data-testid="confirm-cancel" onClick={() => { setConfirmAction(null); setBanReason(''); }} className="flex-1 text-xs py-2 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors">Cancel</button>
              <button data-testid="confirm-action" onClick={() => handleBan(confirmAction.userId)} disabled={banning || !banReason.trim()} className="flex-1 text-xs py-2 rounded-xl font-medium transition-colors disabled:opacity-40 bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30">
                {banning ? 'Banning...' : 'Ban User'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Test Data Cleanup Section */}
      <div className="mt-6 border-t border-white/10 pt-4">
        <div className="flex items-center gap-2 mb-3">
          <Trash2 className="w-4 h-4 text-red-400/60" />
          <h3 className="text-xs font-heading font-semibold text-muted-foreground">Test Data Cleanup</h3>
        </div>
        <p className="text-[10px] text-muted-foreground/60 mb-3">
          Scan for data belonging to banned users, then delete it all. This permanently removes their sessions, chats, boards, crew links, and accounts.
        </p>
        <div className="flex gap-2 mb-3">
          <button
            data-testid="scan-test-data-btn"
            onClick={handleScanTestData}
            disabled={scanning}
            className="flex items-center gap-1.5 text-[10px] px-3 py-2 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors disabled:opacity-40"
          >
            <Scan className="w-3 h-3" />
            {scanning ? 'Scanning...' : 'Scan Banned User Data'}
          </button>
        </div>

        {scanResult && !scanResult.deleted_done && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-3">
            <p className="text-[10px] font-medium text-white mb-2">
              Found {scanResult.banned_user_count} banned user{scanResult.banned_user_count !== 1 ? 's' : ''} with {scanResult.total_records} record{scanResult.total_records !== 1 ? 's' : ''}
            </p>
            {scanResult.banned_users?.length > 0 && (
              <div className="space-y-1 mb-2">
                {scanResult.banned_users.map(u => (
                  <div key={u.user_id} className="text-[9px] text-muted-foreground/70 flex items-center gap-1.5">
                    <ShieldClose className="w-2.5 h-2.5 text-red-400/60" />
                    {u.name || u.email}
                  </div>
                ))}
              </div>
            )}
            {Object.entries(scanResult.data_found || {}).length > 0 && (
              <div className="grid grid-cols-2 gap-1 mb-3">
                {Object.entries(scanResult.data_found).map(([coll, count]) => (
                  <div key={coll} className="text-[9px] text-muted-foreground/50">
                    {coll}: <span className="text-amber-400">{count}</span>
                  </div>
                ))}
              </div>
            )}
            {scanResult.total_records > 0 && (
              <button
                data-testid="delete-test-data-btn"
                onClick={handleDeleteTestData}
                disabled={deleting}
                className="w-full flex items-center justify-center gap-1.5 text-[10px] px-3 py-2 rounded-xl bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 transition-colors disabled:opacity-40 font-medium"
              >
                <Trash2 className="w-3 h-3" />
                {deleting ? 'Deleting...' : `Delete All ${scanResult.total_records} Records + ${scanResult.banned_user_count} Users`}
              </button>
            )}
          </div>
        )}

        {scanResult?.deleted_done && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 mb-3">
            <p className="text-[10px] text-green-400 font-medium">
              Cleanup complete. Removed {scanResult.banned_users_removed} banned user{scanResult.banned_users_removed !== 1 ? 's' : ''} and all their data.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
